/**
 * Tiptap Extension for filtering pasted content
 * Only allows specific HTML elements and styles as defined in STAR-748-RI
 */

import type { ChannelMember } from '@src/api/shared/community/channelMemberApiClient';
import { ChannelMemberUserTypeEnum } from '@src/enum/ChannelMemberUserTypeEnum';
import { TENANT_ROLE } from '@src/enum/tenantRoleEnum';
import logger from '@src/utils/logger';
import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';

export interface PasteFilterOptions {
  allowTextFormatting: boolean;
  allowLists: boolean;
  allowLinks: boolean;
  allowBlockquotes: boolean;
  allowMentions: boolean;
  allowHeadings: boolean;
  allowTables: boolean;
  customFilter?: (html: string) => string;
  removeTenantMentions?: boolean;
  tenantUserType?: string;
  channelMembers?: ChannelMember[];
  excludedRoleCodes?: string[];
  getChannelMembers?: () => ChannelMember[];
}

const DEFAULT_OPTIONS: PasteFilterOptions = {
  allowTextFormatting: true,
  allowLists: true,
  allowLinks: true,
  allowBlockquotes: true,
  allowMentions: true,
  allowHeadings: false,
  allowTables: false,
  removeTenantMentions: false,
  channelMembers: [],
  excludedRoleCodes: [TENANT_ROLE.MEMBER],
};

function removeTenantMentions(html: string, channelMembers: ChannelMember[] = []): string {
  if (!channelMembers || channelMembers.length === 0) {
    return html;
  }

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    const mentions = doc.querySelectorAll('a.mention[data-mention-id]');

    mentions.forEach((mention) => {
      const href = mention.getAttribute('href');
      const mentionId = mention.getAttribute('data-mention-id');

      if (!href || !mentionId) {
        return;
      }
      if (!href.includes('/tenant/')) {
        return;
      }

      const userIdMatch = href.match(/\/tenant\/(\d+)/);
      if (!userIdMatch) {
        return;
      }

      const userId = parseInt(userIdMatch[1], 10);
      const member = channelMembers.find(
        (m) => m.user_type === ChannelMemberUserTypeEnum.TENANT_USER && m.id === userId,
      );

      if (!member) {
        const textContent = mention.textContent || '';
        const textNode = doc.createTextNode(textContent);
        mention.replaceWith(textNode);
      }
    });

    return doc.body.innerHTML;
  } catch (error) {
    logger.error('Error removing tenant mentions from paste:', error);
    return html;
  }
}

function sanitizeHTML(html: string, options: PasteFilterOptions): string {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;

  const allowedTags = new Set(['p', 'br', 'span', 'div']);

  if (options.allowTextFormatting) {
    allowedTags.add('strong');
    allowedTags.add('b');
    allowedTags.add('em');
    allowedTags.add('i');
    allowedTags.add('u');
    allowedTags.add('s');
    allowedTags.add('strike');
  }

  if (options.allowLists) {
    allowedTags.add('ul');
    allowedTags.add('ol');
    allowedTags.add('li');
  }

  if (options.allowLinks) {
    allowedTags.add('a');
  }

  if (options.allowBlockquotes) {
    allowedTags.add('blockquote');
  }

  if (options.allowHeadings) {
    allowedTags.add('h1');
    allowedTags.add('h2');
    allowedTags.add('h3');
    allowedTags.add('h4');
    allowedTags.add('h5');
    allowedTags.add('h6');
  }

  if (options.allowTables) {
    allowedTags.add('table');
    allowedTags.add('thead');
    allowedTags.add('tbody');
    allowedTags.add('tr');
    allowedTags.add('th');
    allowedTags.add('td');
  }

  const allowedStyleProperties = new Set(['font-weight', 'font-style', 'text-decoration']);

  const forbiddenTags = new Set([
    'img',
    'image',
    'picture',
    'svg',
    'canvas',
    'video',
    'audio',
    'embed',
    'object',
    'iframe',
    'script',
    'style',
    'link',
    'meta',
    'base',
    'form',
    'input',
    'textarea',
    'button',
    'select',
  ]);

  function cleanElement(element: Element): void {
    const tagName = element.tagName.toLowerCase();

    if (forbiddenTags.has(tagName)) {
      element.parentNode?.removeChild(element);
      return;
    }

    if (!allowedTags.has(tagName)) {
      const textContent = element.textContent || '';
      const textNode = document.createTextNode(textContent);
      element.parentNode?.replaceChild(textNode, element);
      return;
    }

    const allowedAttributes = new Set(['href', 'data-mention-id', 'class']);
    const attributesToRemove: string[] = [];

    for (let i = 0; i < element.attributes.length; i++) {
      const attr = element.attributes[i];
      if (!allowedAttributes.has(attr.name)) {
        attributesToRemove.push(attr.name);
      }
    }

    attributesToRemove.forEach((attr) => element.removeAttribute(attr));

    if (element.hasAttribute('style')) {
      const style = element.getAttribute('style') || '';
      const allowedStyles: string[] = [];

      const styleValidators = {
        'text-decoration': (value: string) => ['underline', 'line-through', 'none'].includes(value),
        'font-weight': (value: string) => ['bold', 'normal', '400', '600', '700'].includes(value),
        'font-style': (value: string) => ['italic', 'normal'].includes(value),
      } as const;

      style.split(';').forEach((declaration) => {
        const [property, value] = declaration.split(':').map((s) => s.trim());

        if (!property || !value || !allowedStyleProperties.has(property)) {
          return;
        }

        const validator = styleValidators[property as keyof typeof styleValidators];
        if (validator?.(value)) {
          allowedStyles.push(`${property}: ${value}`);
        }
      });

      if (allowedStyles.length > 0) {
        element.setAttribute('style', allowedStyles.join('; '));
      } else {
        element.removeAttribute('style');
      }
    }

    element.removeAttribute('onclick');
    element.removeAttribute('onload');
    element.removeAttribute('onerror');

    const children = Array.from(element.children);
    children.forEach((child) => {
      if (child.parentNode) {
        cleanElement(child);
      }
    });
  }

  // Clean all elements (process from bottom up to avoid issues with removed elements)
  const rootChildren = Array.from(tempDiv.children);
  rootChildren.forEach((child) => cleanElement(child));

  let cleanedHTML = tempDiv.innerHTML;

  // Apply custom filter if provided
  if (options.customFilter) {
    cleanedHTML = options.customFilter(cleanedHTML);
  }

  // Additional cleanup: remove empty elements and normalize whitespace
  cleanedHTML = cleanedHTML
    .replace(/<(\w+)(?:[^>]*?)>\s*<\/\1>/g, '') // Remove empty tags
    .replace(/\s+/g, ' ') // Normalize whitespace
    .trim();

  return cleanedHTML;
}

/**
 * Converts inline styles to semantic HTML elements
 */
function convertInlineStylesToSemanticHTML(html: string): string {
  let processed = html;

  // Convert text-decoration: line-through to <s> tag
  processed = processed.replace(
    /<(\w+)([^>]*?)style="([^"]*?)text-decoration:\s*line-through([^"]*?)"([^>]*?)>(.*?)<\/\1>/gi,
    (_match, tag, beforeStyle, beforeDecoration, afterDecoration, afterStyle, content) => {
      const cleanedStyle = (beforeDecoration + afterDecoration)
        .replace(/;\s*$/, '')
        .replace(/^\s*;/, '');
      if (cleanedStyle.trim()) {
        return `<s><${tag}${beforeStyle}style="${cleanedStyle}"${afterStyle}>${content}</${tag}></s>`;
      } else {
        return `<s><${tag}${beforeStyle}${afterStyle}>${content}</${tag}></s>`;
      }
    },
  );

  // Convert font-weight: bold to <strong> tag
  processed = processed.replace(
    /<(\w+)([^>]*?)style="([^"]*?)font-weight:\s*(bold|600|700)([^"]*?)"([^>]*?)>(.*?)<\/\1>/gi,
    (_match, tag, beforeStyle, beforeWeight, _weight, afterWeight, afterStyle, content) => {
      const cleanedStyle = (beforeWeight + afterWeight).replace(/;\s*$/, '').replace(/^\s*;/, '');
      if (cleanedStyle.trim()) {
        return `<strong><${tag}${beforeStyle}style="${cleanedStyle}"${afterStyle}>${content}</${tag}></strong>`;
      } else {
        return `<strong><${tag}${beforeStyle}${afterStyle}>${content}</${tag}></strong>`;
      }
    },
  );

  // Convert font-style: italic to <em> tag
  processed = processed.replace(
    /<(\w+)([^>]*?)style="([^"]*?)font-style:\s*italic([^"]*?)"([^>]*?)>(.*?)<\/\1>/gi,
    (_match, tag, beforeStyle, beforeItalic, afterItalic, afterStyle, content) => {
      const cleanedStyle = (beforeItalic + afterItalic).replace(/;\s*$/, '').replace(/^\s*;/, '');
      if (cleanedStyle.trim()) {
        return `<em><${tag}${beforeStyle}style="${cleanedStyle}"${afterStyle}>${content}</${tag}></em>`;
      } else {
        return `<em><${tag}${beforeStyle}${afterStyle}>${content}</${tag}></em>`;
      }
    },
  );

  return processed;
}

/**
 * PasteFilter Extension
 * Filters clipboard content to only allow specific HTML elements and styles
 */
export const PasteFilter = Extension.create<PasteFilterOptions>({
  name: 'pasteFilter',

  addOptions() {
    return DEFAULT_OPTIONS;
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('pasteFilter'),
        props: {
          transformPastedHTML: (html: string) => {
            try {
              let processedHTML = html;
              if (this.options.removeTenantMentions) {
                const members = this.options.getChannelMembers
                  ? this.options.getChannelMembers()
                  : this.options.channelMembers || [];

                processedHTML = removeTenantMentions(processedHTML, members);
              }

              processedHTML = convertInlineStylesToSemanticHTML(processedHTML);

              processedHTML = sanitizeHTML(processedHTML, this.options);

              return processedHTML;
            } catch (error) {
              logger.warn(
                'PasteFilter: Error processing pasted HTML, falling back to plain text',
                error,
              );
              // Fallback: strip all HTML and return plain text
              const tempDiv = document.createElement('div');
              tempDiv.innerHTML = html;
              return tempDiv.textContent || tempDiv.innerText || '';
            }
          },

          transformPastedText: (text: string) => {
            // For plain text, just ensure it's clean
            return text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
          },
        },
      }),
    ];
  },
});

export default PasteFilter;
